import { z } from "zod";
import { Id, IsoDateTime } from "./common";

/**
 * Facility hierarchy: Site -> Building -> Floor.
 *
 * Per `designs/asset_monitoring_mvp_physical_data_implementation.md` §7.5–7.8.
 * Soft-delete via {@link FacilityStatus}; physical `delete` is reserved for
 * out-of-band cleanup (Phase 7 reset CLI).
 */
export const FacilityStatus = z.enum([
  "Active",
  "Disabled",
  "Archived",
  "Retired",
]);
export type FacilityStatus = z.infer<typeof FacilityStatus>;

export const SiteType = z.enum([
  "Facility",
  "Campus",
  "Yard",
  "Warehouse",
  "Other",
]);
export type SiteType = z.infer<typeof SiteType>;

export const LocationSource = z.enum([
  "Manual",
  "Geocoded",
  "Integration",
  "Unknown",
]);
export type LocationSource = z.infer<typeof LocationSource>;

export const LocationConfidence = z.enum(["High", "Medium", "Low", "Unknown"]);
export type LocationConfidence = z.infer<typeof LocationConfidence>;

const Latitude = z.number().min(-90).max(90).nullable();
const Longitude = z.number().min(-180).max(180).nullable();

export const Site = z.object({
  siteId: Id,
  tenantId: Id,
  name: z.string().min(1).max(120),
  normalizedName: z.string().min(1).max(120),
  siteType: SiteType,
  status: FacilityStatus,
  addressLine1: z.string().max(200).nullable(),
  addressLine2: z.string().max(200).nullable(),
  city: z.string().max(120).nullable(),
  stateRegion: z.string().max(120).nullable(),
  postalCode: z.string().max(40).nullable(),
  country: z.string().max(2).nullable(),
  latitude: Latitude,
  longitude: Longitude,
  locationSource: LocationSource,
  locationConfidence: LocationConfidence,
  timezone: z.string().max(64).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type Site = z.infer<typeof Site>;

export const SiteCreateInput = z.object({
  name: z.string().min(1).max(120),
  siteType: SiteType,
  addressLine1: z.string().max(200).nullable().optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  stateRegion: z.string().max(120).nullable().optional(),
  postalCode: z.string().max(40).nullable().optional(),
  country: z.string().max(2).nullable().optional(),
  latitude: Latitude.optional(),
  longitude: Longitude.optional(),
  locationSource: LocationSource.optional(),
  locationConfidence: LocationConfidence.optional(),
  timezone: z.string().max(64).nullable().optional(),
});
export type SiteCreateInput = z.infer<typeof SiteCreateInput>;

export const SiteUpdateInput = z
  .object({
    name: z.string().min(1).max(120),
    siteType: SiteType,
    status: FacilityStatus,
    addressLine1: z.string().max(200).nullable(),
    addressLine2: z.string().max(200).nullable(),
    city: z.string().max(120).nullable(),
    stateRegion: z.string().max(120).nullable(),
    postalCode: z.string().max(40).nullable(),
    country: z.string().max(2).nullable(),
    latitude: Latitude,
    longitude: Longitude,
    locationSource: LocationSource,
    locationConfidence: LocationConfidence,
    timezone: z.string().max(64).nullable(),
  })
  .partial();
export type SiteUpdateInput = z.infer<typeof SiteUpdateInput>;

export const Building = z.object({
  buildingId: Id,
  tenantId: Id,
  siteId: Id,
  name: z.string().min(1).max(120),
  normalizedName: z.string().min(1).max(120),
  status: FacilityStatus,
  latitude: Latitude,
  longitude: Longitude,
  locationSource: LocationSource,
  locationConfidence: LocationConfidence,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type Building = z.infer<typeof Building>;

export const BuildingCreateInput = z.object({
  name: z.string().min(1).max(120),
  latitude: Latitude.optional(),
  longitude: Longitude.optional(),
  locationSource: LocationSource.optional(),
  locationConfidence: LocationConfidence.optional(),
});
export type BuildingCreateInput = z.infer<typeof BuildingCreateInput>;

export const BuildingUpdateInput = z
  .object({
    name: z.string().min(1).max(120),
    status: FacilityStatus,
    latitude: Latitude,
    longitude: Longitude,
    locationSource: LocationSource,
    locationConfidence: LocationConfidence,
  })
  .partial();
export type BuildingUpdateInput = z.infer<typeof BuildingUpdateInput>;

export const Floor = z.object({
  floorId: Id,
  tenantId: Id,
  siteId: Id,
  buildingId: Id,
  name: z.string().min(1).max(120),
  floorNumber: z.number().int().min(-50).max(500).nullable(),
  status: FacilityStatus,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type Floor = z.infer<typeof Floor>;

export const FloorCreateInput = z.object({
  name: z.string().min(1).max(120),
  floorNumber: z.number().int().min(-50).max(500).nullable().optional(),
});
export type FloorCreateInput = z.infer<typeof FloorCreateInput>;

export const FloorUpdateInput = z
  .object({
    name: z.string().min(1).max(120),
    floorNumber: z.number().int().min(-50).max(500).nullable(),
    status: FacilityStatus,
  })
  .partial();
export type FloorUpdateInput = z.infer<typeof FloorUpdateInput>;
